/**
 * The three things people need when something has gone wrong.
 *
 * Where a report of mine stands, a way to tell somebody the app itself is
 * broken, and - for whoever is running a party - what has actually been done
 * in it and by whom. All three are about the same thing: a person who has
 * done something and cannot tell whether it worked.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { PublicUser } from '@/lib/types';
import { cn } from '@/lib/cn';
import { ago } from '@/lib/notifications';
import { isDesktop } from '@/lib/bridge';
import { useSession } from '@/stores/session';
import { Icon } from '@/icons/Icon';
import { Avatar } from './Avatar';
import { UserName } from './user';
import { Button, Card, CardHead, Field, Modal, useToast } from './ui';

/* ----------------------------------------------------------- my reports */

type MyReport = {
  id: string;
  kind: string;
  about: string;
  reason: string;
  createdAt: number;
  status: 'waiting' | 'acted' | 'closed';
  resolvedAt: number | null;
};

const STATUS: Record<string, { label: string; note: string; tone: string; icon: string }> = {
  waiting: { label: 'Waiting', note: 'With the moderators. Nobody has closed it yet.', tone: 'text-warn', icon: 'hourglass' },
  acted: { label: 'Acted on', note: 'A moderator looked at it and did something.', tone: 'text-good', icon: 'checkCirc' },
  closed: { label: 'Closed', note: 'Looked at, and no action was needed.', tone: 'text-mute', icon: 'check' }
};

export function MyReports() {
  const q = useQuery({
    queryKey: ['my-reports'],
    queryFn: () => api.get<{ reports: MyReport[] }>('/api/reports/mine'),
    staleTime: 60_000
  });
  const list = q.data?.reports ?? [];

  return (
    <Card>
      <CardHead title="Reports you have sent" />
      <p className="m-0 mb-3 text-[14px] text-mute">
        Where each one stands. What a moderator decided about somebody else&apos;s account is not shown - that is theirs, not yours.
      </p>

      {q.isPending ? (
        <div className="flex flex-col gap-2">{[0, 1].map((i) => <div key={i} className="h-[52px] animate-pulse rounded-[12px] bg-surface-2" />)}</div>
      ) : !list.length ? (
        <p className="m-0 text-[13.5px] text-mute">You have not reported anything.</p>
      ) : (
        <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
          {list.map((r) => {
            const look = STATUS[r.status] ?? STATUS.waiting!;
            return (
              <li key={r.id} className="flex flex-wrap items-center gap-2.5 rounded-[calc(12px*var(--radius-mul,1))] bg-surface-2 p-2.5">
                <Icon name={look.icon} size={16} className={cn('flex-none', look.tone)} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13.5px] font-semibold text-ink">
                    {r.kind === 'party' ? 'A party' : r.kind === 'crew' ? 'A crew' : r.kind === 'message' ? 'A message' : 'A player'}
                    {r.about ? ' · ' + r.about : ''}
                  </span>
                  <span className="block truncate text-[12px] text-mute">{r.reason || 'No reason given'} · sent {ago(r.createdAt)}</span>
                </span>
                <span className={cn('flex-none text-[12px] font-bold', look.tone)}>{look.label}</span>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

/* ------------------------------------------------------- reporting a bug */

/**
 * Telling us the app is broken.
 *
 * The diagnostic lines are shown before anything is sent, because a person
 * handing over their app version and window size deserves to see exactly
 * that list rather than the word "diagnostics".
 */
export function ReportProblem() {
  const [open, setOpen] = useState(false);
  return (
    <Card>
      <CardHead title="Something is broken" />
      <p className="m-0 mb-3 text-[14px] text-mute">Tell us what happened. You can read what gets sent with it first.</p>
      <Button kind="outline" icon={<Icon name="bug" size={16} />} onClick={() => setOpen(true)}>Report a problem</Button>
      {open ? <ProblemDialog onClose={() => setOpen(false)} /> : null}
    </Card>
  );
}

function ProblemDialog({ onClose }: { onClose: () => void }) {
  const me = useSession((s) => s.me);
  const toast = useToast();
  const [what, setWhat] = useState('');
  const [include, setInclude] = useState(true);
  const [busy, setBusy] = useState(false);

  /* Exactly what would be attached, in the order it would be sent. */
  const facts: [string, string][] = [
    ['App', (isDesktop() ? 'Desktop app' : 'Browser') + ' · ' + navigator.userAgent.slice(0, 60)],
    ['Screen', window.innerWidth + '×' + window.innerHeight],
    ['Where you are', location.hash || '#/home'],
    ['Language', navigator.language],
    ['Account', me ? '@' + me.username : 'signed out']
  ];

  async function send() {
    if (!what.trim()) { toast('Say what went wrong.', 'bad'); return; }
    setBusy(true);
    try {
      await api.post('/api/reports', {
        kind: 'bug',
        reason: 'bug',
        note: what.trim() + (include ? '\n\n---\n' + facts.map(([k, v]) => k + ': ' + v).join('\n') : '')
      });
      toast('Sent. Thank you - this goes to the people who can fix it.', 'good');
      onClose();
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Report a problem"
      subtitle="What were you doing, and what happened instead?"
      onClose={onClose}
      footer={
        <>
          <Button kind="outline" onClick={onClose}>Cancel</Button>
          <Button data-primary kind="primary" disabled={busy} onClick={() => void send()}>{busy ? 'Sending…' : 'Send'}</Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="What happened" hint={what.length + '/800'}>
          <textarea
            autoFocus
            rows={5}
            value={what}
            onChange={(e) => setWhat(e.target.value.slice(0, 800))}
            placeholder="I pressed Join on a party and nothing happened."
            className="w-full resize-none rounded-[var(--radius-s)] border border-line bg-surface-1 p-3 text-[13.5px] outline-none transition-colors placeholder:text-ghost focus:border-accent"
          />
        </Field>

        <div className="rounded-[calc(12px*var(--radius-mul,1))] bg-surface-inset p-3">
          <label className="flex items-center gap-2.5 text-[13.5px] font-semibold text-ink">
            <input type="checkbox" checked={include} onChange={(e) => setInclude(e.target.checked)} className="h-4 w-4 accent-[var(--accent)]" />
            Include these details
          </label>
          <dl className="m-0 mt-2 grid gap-1">
            {facts.map(([k, v]) => (
              <div key={k} className="flex gap-2 text-[12.5px]">
                <dt className="m-0 w-[110px] flex-none text-mute">{k}</dt>
                <dd className="m-0 min-w-0 flex-1 truncate font-mono text-dim">{v}</dd>
              </div>
            ))}
          </dl>
          <p className="m-0 mt-2 text-[11.5px] text-ghost">No messages, no friends, no password. Nothing beyond this list.</p>
        </div>
      </div>
    </Modal>
  );
}

/* ---------------------------------------------------------- party log */

type LogRow = { at: number; action: string; by: PublicUser | null; target: PublicUser | null; detail: string };

const ACTION_TEXT: Record<string, string> = {
  'party.kick': 'removed',
  'party.mute': 'muted',
  'party.unmute': 'unmuted',
  'party.role': 'changed the role of',
  'party.owner': 'handed the party to',
  'admin.party_close': 'closed the party',
  'admin.party_boost': 'boosted the party'
};

/** What has been done in this party, for whoever is running it. */
export function PartyLog({ partyId, onClose }: { partyId: string; onClose: () => void }) {
  const q = useQuery({
    queryKey: ['party-log', partyId],
    queryFn: () => api.get<{ log: LogRow[] }>('/api/parties/' + partyId + '/log')
  });
  const rows = q.data?.log ?? [];

  return (
    <Modal
      title="What has been done here"
      subtitle="Removals, mutes, role changes and handovers. Shared administration only works if it is visible."
      onClose={onClose}
      footer={<Button kind="outline" onClick={onClose}>Close</Button>}
    >
      {q.isPending ? (
        <div className="flex flex-col gap-2">{[0, 1, 2].map((i) => <div key={i} className="h-[46px] animate-pulse rounded-[12px] bg-surface-2" />)}</div>
      ) : !rows.length ? (
        <p className="m-0 py-6 text-center text-[13.5px] text-mute">Nothing has been done in this party.</p>
      ) : (
        <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
          {rows.map((row, i) => (
            <li key={i} className="flex items-center gap-2.5 rounded-[calc(12px*var(--radius-mul,1))] bg-surface-2 p-2.5">
              {row.by ? <Avatar user={row.by} size={26} /> : null}
              <span className="min-w-0 flex-1 text-[13px] text-dim">
                {row.by ? <UserName user={row.by} className="font-semibold text-ink" /> : <span className="font-semibold text-ink">Somebody</span>}
                {' '}{ACTION_TEXT[row.action] ?? row.action}{' '}
                {row.target ? <UserName user={row.target} className="font-semibold text-ink" /> : null}
                {row.detail ? <span className="text-mute"> · {row.detail}</span> : null}
              </span>
              <span className="flex-none text-[11.5px] text-ghost">{ago(row.at)}</span>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}
